import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import { BENCHMARK_BASELINE, ENGAGEMENT, ENGAGEMENT_MULTIPLE } from '@/components/marketing/siteFacts';
import {
  ArrowLeft,
  Calculator,
  DollarSign,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Building2,
  Heart,
  Briefcase
} from 'lucide-react';

/**
 * Public SDOH ROI Calculator
 * Interactive tool to estimate potential savings from addressing social determinants of health
 */
export default function ROICalculatorPage() {
  usePageMeta(SEO_ROUTES['/roi-calculator']);

  const currentYear = new Date().getFullYear();

  const [inputs, setInputs] = useState({
    employeeCount: 500,
    avgSalary: 65000,
    turnoverCostPercent: 50, // % of salary
    atRiskPercent: 15, // % of employees at risk
    engagementRate: 19, // % expected to engage; defaults to the Medi-Cal cohort rate (siteFacts ENGAGEMENT)
    // Was hardcoded at 65% and labelled "industry data" with no source. It is
    // the single assumption the whole result swings on, so it is the user's to
    // set rather than ours to assert.
    retentionRate: 65, // % of engaged at-risk employees expected to stay
    programCostPerEmployee: 500, // Annual program cost
  });

  // Calculate ROI metrics
  const calculations = useMemo(() => {
    const atRiskEmployees = Math.floor(inputs.employeeCount * (inputs.atRiskPercent / 100));
    const turnoverCostPerEmployee = inputs.avgSalary * (inputs.turnoverCostPercent / 100);
    const potentialTurnoverLoss = atRiskEmployees * turnoverCostPerEmployee;
    const engagedEmployees = Math.floor(atRiskEmployees * (inputs.engagementRate / 100));
    const retentionRate = inputs.retentionRate / 100;
    const employeesRetained = Math.floor(engagedEmployees * retentionRate);
    const savingsFromRetention = employeesRetained * turnoverCostPerEmployee;
    const programCost = inputs.employeeCount * inputs.programCostPerEmployee;
    const netSavings = savingsFromRetention - programCost;
    const roi = programCost > 0 ? ((savingsFromRetention - programCost) / programCost) * 100 : 0;

    // Additional benefits estimates (conservative)
    const productivityGain = engagedEmployees * 2500; // $2,500 per engaged employee
    const healthcareSavings = engagedEmployees * 1200; // $1,200 per engaged employee
    const absenteeismSavings = engagedEmployees * 800; // $800 per engaged employee

    const totalBenefits = savingsFromRetention + productivityGain + healthcareSavings + absenteeismSavings;
    const totalROI = programCost > 0 ? ((totalBenefits - programCost) / programCost) * 100 : 0;

    return {
      atRiskEmployees,
      turnoverCostPerEmployee,
      potentialTurnoverLoss,
      engagedEmployees,
      employeesRetained,
      savingsFromRetention,
      programCost,
      netSavings,
      roi,
      productivityGain,
      healthcareSavings,
      absenteeismSavings,
      totalBenefits,
      totalROI,
    };
  }, [inputs]);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper border-b border-v2border py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-v2accent-soft rounded-2xl mb-6">
            <Calculator className="w-8 h-8 text-ink" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-ink mb-4">
            SDOH ROI Calculator
          </h1>
          <p className="text-xl text-ink-500 max-w-2xl mx-auto">
            Estimate your potential savings from addressing social determinants of health in your workforce
          </p>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Inputs Panel */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-ink dark:text-white mb-6 flex items-center">
                <Building2 className="w-6 h-6 mr-3" />
                Your Organization
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Number of Employees
                  </label>
                  <input
                    type="number"
                    value={inputs.employeeCount}
                    onChange={(e) => setInputs({ ...inputs, employeeCount: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Average Annual Salary ($)
                  </label>
                  <input
                    type="number"
                    value={inputs.avgSalary}
                    onChange={(e) => setInputs({ ...inputs, avgSalary: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Turnover Cost (% of Salary)
                  </label>
                  <input
                    type="number"
                    value={inputs.turnoverCostPercent}
                    onChange={(e) => setInputs({ ...inputs, turnoverCostPercent: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Industry average: 50-200% depending on role
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Estimated At-Risk Employees (%)
                  </label>
                  <input
                    type="number"
                    value={inputs.atRiskPercent}
                    onChange={(e) => setInputs({ ...inputs, atRiskPercent: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    National average: 15-25% face SDOH challenges
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Expected Program Engagement (%)
                  </label>
                  <input
                    type="number"
                    value={inputs.engagementRate}
                    onChange={(e) => setInputs({ ...inputs, engagementRate: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Medi-Cal cohort engagement: {ENGAGEMENT.value}, {ENGAGEMENT_MULTIPLE.value} the {BENCHMARK_BASELINE}% CHCS baseline. Set your own.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Retention Among Engaged At-Risk Employees (%)
                  </label>
                  <input
                    type="number"
                    value={inputs.retentionRate}
                    onChange={(e) => setInputs({ ...inputs, retentionRate: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Your assumption. Set it to what your own turnover data supports.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Annual Program Cost per Employee ($)
                  </label>
                  <input
                    type="number"
                    value={inputs.programCostPerEmployee}
                    onChange={(e) => setInputs({ ...inputs, programCostPerEmployee: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-v2accent focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Results Panel */}
            <div className="space-y-6">
              {/* Primary ROI Card */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-8 border border-green-200 dark:border-green-800">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  Projected Annual ROI
                </h3>
                <div className="text-5xl font-bold text-green-600 mb-2">
                  {calculations.totalROI.toFixed(0)}%
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Total return on your SDOH investment
                </p>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-amber-600 mb-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="text-sm font-medium">At-Risk</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {calculations.atRiskEmployees}
                  </div>
                  <p className="text-sm text-gray-500">employees</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-blue-600 mb-2">
                    <Users className="w-5 h-5" />
                    <span className="text-sm font-medium">Engaged</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {calculations.engagedEmployees}
                  </div>
                  <p className="text-sm text-gray-500">employees</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-green-600 mb-2">
                    <CheckCircle className="w-5 h-5" />
                    <span className="text-sm font-medium">Retained</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {calculations.employeesRetained}
                  </div>
                  <p className="text-sm text-gray-500">employees saved</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 text-v2accent-ink mb-2">
                    <DollarSign className="w-5 h-5" />
                    <span className="text-sm font-medium">Investment</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(calculations.programCost)}
                  </div>
                  <p className="text-sm text-gray-500">annual cost</p>
                </div>
              </div>

              {/* Savings Breakdown */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-ink dark:text-white mb-4 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Projected Annual Savings
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Retention Savings</span>
                    <span className="font-semibold text-green-600">{formatCurrency(calculations.savingsFromRetention)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Productivity Gains</span>
                    <span className="font-semibold text-green-600">{formatCurrency(calculations.productivityGain)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Healthcare Savings</span>
                    <span className="font-semibold text-green-600">{formatCurrency(calculations.healthcareSavings)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Absenteeism Reduction</span>
                    <span className="font-semibold text-green-600">{formatCurrency(calculations.absenteeismSavings)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                    <span className="text-gray-600 dark:text-gray-400">Program Investment</span>
                    <span className="font-semibold text-red-500">-{formatCurrency(calculations.programCost)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2">
                    <span className="font-bold text-gray-900 dark:text-white">Net Annual Benefit</span>
                    <span className="text-2xl font-bold text-green-600">
                      {formatCurrency(calculations.totalBenefits - calculations.programCost)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Context */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-ink dark:text-white text-center mb-12">
            Why Address Social Determinants?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-v2accent-soft rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-v2accent-ink" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">80%</h3>
              <p className="text-gray-600 dark:text-gray-300">
                of health outcomes are driven by social and environmental factors, not medical care
              </p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">40%</h3>
              <p className="text-gray-600 dark:text-gray-300">
                of employees facing SDOH challenges are likely to leave within 12 months
              </p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">259%</h3>
              <p className="text-gray-600 dark:text-gray-300">
                ROI reported in one CalAIM program, first published on trust.care
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-paper border-b border-v2border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-ink mb-4">
            Ready to See These Results?
          </h2>
          <p className="text-xl text-ink-500 mb-8">
            Schedule a demo to learn how WeaveIn can help you achieve these outcomes for your organization.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center bg-ink text-paper px-8 py-4 rounded-lg hover:bg-ink-700 transition-colors font-semibold text-lg"
          >
            Request a Demo
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
